import React from 'react';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { PermissionCheck, useAuth } from 'common/context/AuthContext';

interface SidebarLinkProps {
	to: string;
	label: string;
	icon: IconProp;
	matchExact?: boolean;
	inactive?: boolean;
	onClick?: () => void; // pour fermer les menus ouverts si nécessaire
	className?: string;
	/**
	 * Module unique requis en lecture pour accéder à ce lien.
	 * Raccourci pour requiredPermissions: [{ module, action: 'read' }].
	 * Si omis, le lien est toujours accessible.
	 */
	requiredModule?: string;
	/**
	 * Liste de permissions requises (logique AND).
	 * Chaque entrée spécifie un module et une action (défaut: 'read').
	 * Toutes les conditions doivent être satisfaites pour que le lien soit actif.
	 * Prend le dessus sur requiredModule si les deux sont fournis.
	 */
	requiredPermissions?: PermissionCheck[];
}

const SidebarLink: React.FC<SidebarLinkProps> = ({
	to,
	label,
	icon,
	matchExact,
	inactive,
	onClick,
	className = '',
	requiredModule,
	requiredPermissions,
}) => {
	const { canRead, canAll, permissionsLoaded } = useAuth();

	// Calcul de l'état désactivé
	let isDisabled = inactive ?? false;
	if (!isDisabled && permissionsLoaded) {
		if (requiredPermissions !== undefined) {
			// Vérification multi-permissions (AND)
			isDisabled = !canAll(requiredPermissions);
		} else if (requiredModule !== undefined) {
			// Raccourci : lecture seule sur un module
			isDisabled = !canRead(requiredModule);
		}
	}

	return (
		<NavLink
			to={to}
			end={matchExact ?? true}
			onClick={(e) => {
				if (isDisabled) {
					// Bloquer la navigation si le lien est grisé
					e.preventDefault();
					return;
				}
				onClick?.();
			}}
			className={({ isActive }) =>
				`text-gray-300 p-2 rounded flex items-center gap-2 text-md transition-colors ${
					isDisabled
						? 'text-gray-600 cursor-not-allowed opacity-50 hover:bg-transparent pointer-events-none'
						: isActive
							? 'bg-gray-700 text-white'
							: 'hover:bg-gray-600'
				} ${className}`
			}
			title={
				isDisabled && (requiredModule ?? requiredPermissions)
					? "Vous n'avez pas accès à ce module."
					: undefined
			}
			aria-disabled={isDisabled}
		>
			<FontAwesomeIcon icon={icon} className="w-4 h-4" /> {label}
		</NavLink>
	);
};

export default SidebarLink;
